import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useDisplay from "../../../../hooks/useDisplay";

const TransparentPopup = ({ contents, onClose, onDontShowToday }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [failedImages, setFailedImages] = useState({});

  const { isMobile } = useDisplay();

  // 이미지 로드 실패 시 fallback 이미지 사용
  const getImageSrc = (content, index) => {
    if (!content?.image) return "";

    const imageKey = `${index}-${isMobile ? "mobile" : "pc"}`;
    const hasFailed = failedImages[imageKey];

    if (isMobile) {
      return hasFailed && content.image.mobileFallback
        ? content.image.mobileFallback
        : content.image.mobile || "";
    } else {
      return hasFailed && content.image.pcFallback
        ? content.image.pcFallback
        : content.image.pc || "";
    }
  };

  const handleImageError = (index) => {
    const imageKey = `${index}-${isMobile ? "mobile" : "pc"}`;
    setFailedImages((prev) => ({ ...prev, [imageKey]: true }));
  };

  useEffect(() => {
    const seenUntil = localStorage.getItem("popupSeenToday");
    if (seenUntil) {
      let expiresAt;
      if (seenUntil === "true") {
        // 구버전 호환: "true"가 저장된 경우 다음 자정까지 유효한 것으로 처리
        expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 1);
        expiresAt.setHours(0, 0, 0, 0);
        localStorage.setItem("popupSeenToday", expiresAt.toISOString());
      } else {
        expiresAt = new Date(seenUntil);
      }
      if (!Number.isNaN(expiresAt.getTime())) {
        if (expiresAt > new Date()) {
          setIsOpen(false);
        } else {
          localStorage.removeItem("popupSeenToday");
        }
      } else {
        localStorage.removeItem("popupSeenToday");
      }
    }
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : contents.length - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev < contents.length - 1 ? prev + 1 : 0));
  };

  const handleDontShowToday = () => {
    const expires = new Date();
    expires.setDate(expires.getDate() + 1);
    expires.setHours(0, 0, 0, 0);
    localStorage.setItem("popupSeenToday", expires.toISOString());
    onDontShowToday();
    setIsOpen(false);
  };

  const handleClose = () => {
    onClose();
    setIsOpen(false);
  };

  const handleImageClick = () => {
    const currentContent = contents[currentSlide];
    // IAC2025 팝업인 경우 S3 이미지를 새창에서 열기
    if (currentContent?.alt?.includes("IAC2025")) {
      const imageUrl =
        "https://seoyeon-private-bucket.s3.ap-northeast-2.amazonaws.com/%ED%96%89%EC%82%AC%EA%B4%80%EB%A0%A8/%EB%B6%80%EC%8A%A4+%EC%95%88%EB%82%B4+%EB%A9%94%EC%9D%BC.jpg";
      window.open(imageUrl, "_blank");
    }
  };

  if (!isOpen || !contents?.length) return null;

  return (
    <PopupOverlay $isMobile={isMobile}>
      <PopupContent $isMobile={isMobile}>
        <ImageSlider $isMobile={isMobile}>
          <SliderImage
            src={getImageSrc(contents[currentSlide], currentSlide)}
            alt={contents[currentSlide]?.alt || ""}
            onError={() => handleImageError(currentSlide)}
            onClick={handleImageClick}
            $isClickable={contents[currentSlide]?.alt?.includes("IAC2025")}
          />
          {contents.length > 1 && (
            <>
              <SliderButton
                onClick={handlePrevSlide}
                aria-label="이전 슬라이드"
                $left
              >
                &#10094;
              </SliderButton>
              <SliderButton
                onClick={handleNextSlide}
                aria-label="다음 슬라이드"
                $right
              >
                &#10095;
              </SliderButton>
            </>
          )}
        </ImageSlider>
        <NavigationDots>
          {contents.map((_, index) => (
            <Dot
              key={index}
              $active={index === currentSlide}
              onClick={() => setCurrentSlide(index)}
              className="custom-cursor"
            />
          ))}
        </NavigationDots>
        <ButtonContainer>
          <PopupButton onClick={handleDontShowToday}>
            오늘 하루 보지 않기
          </PopupButton>
          <PopupButton onClick={handleClose}>닫기</PopupButton>
        </ButtonContainer>
      </PopupContent>
    </PopupOverlay>
  );
};

export default TransparentPopup;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  right: ${(props) => (props.$isMobile ? "0" : "5%")};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: ${(props) => (props.$isMobile ? "center" : "flex-end")};
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background-color: rgba(114, 114, 114, 0.3);
  padding: 15px;
  border-radius: 10px;
  position: relative;
  max-width: 90%;
  width: ${(props) => (props.$isMobile ? "335px" : "600px")};
  backdrop-filter: blur(10px);
`;

const ImageSlider = styled.div`
  position: relative;
  width: 100%;
  padding-top: ${(props) => (props.$isMobile ? "100%" : "56.25%")};
  overflow: hidden;
`;

const SliderImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  opacity: 0.85;
  cursor: ${(props) => (props.$isClickable ? "pointer" : "default")};

  &:hover {
    opacity: ${(props) => (props.$isClickable ? "1" : "0.85")};
  }
`;

const SliderButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 10px;
  cursor: none;
  border-radius: 10px;
  ${(props) => props.$left && "left: 10px;"}
  ${(props) => props.$right && "right: 10px;"}
`;

const NavigationDots = styled.div`
  text-align: center;
  margin-top: 10px;
`;

const Dot = styled.span`
  height: 10px;
  width: 10px;
  background-color: ${(props) => (props.$active ? "white" : "#bbb")};
  border-radius: 50%;
  display: inline-block;
  margin: 0 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 14px;
`;

const PopupButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: none;
  padding: 5px 10px;
  font-size: 14px;
`;
